import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Subject } from 'rxjs';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import { IPBoardHelper } from 'src/app/helpers/ip-board-helper';
import ProductHelper from 'src/app/helpers/product-helper';
import ProjectHelper from 'src/app/helpers/project-helper';
import Asset from 'src/app/model/asset';
import Company from 'src/app/model/company/company';
import ProjectContact from 'src/app/model/company/projectContact';
import Contact from 'src/app/model/Contact/contact';
import ProductAsset from 'src/app/model/product-asset';
import IPBoard from 'src/app/model/product/ip-board/ip-board';
import Product from 'src/app/model/product/product';
import { BaseProject } from 'src/app/model/project/base-project';
import { Project } from 'src/app/model/project/project';
import { ProjectExtraCompanyContact } from 'src/app/model/project/project-extra-company-contact';
import { FormatPhoneNumber } from 'src/app/modules/app-pipes/format-phonenumber';
import { GroupProjectContactPipe } from 'src/app/pipes/group-project-contact';
import { PrintAddressPipe } from 'src/app/pipes/print-address.pipe';
import {
  NotExecutedHistoryItemAssetsFetchedEvent,
  ProductAssetsChangedEvent,
  ProjectDisplayObservableService,
} from 'src/app/services/project-display-observable.service';
import { SubscriptionWithDestroyTriggerComponent } from 'src/app/subscription-helpers/subscription-with-destroy-trigger.component';
import { formatFirstProduct } from '../../../helpers/first-product/first-product-helper';
import { ClientBaseProjectSummaryHelper } from './helper/client-base-project-summary-helper';

@Component({
  selector: 'client-base-project-summary',
  templateUrl: './client-base-project-summary.component.html',
  styleUrls: ['./client-base-project-summary.component.scss'],
  providers: [GroupProjectContactPipe, FormatPhoneNumber, PrintAddressPipe],
})
export class ClientBaseProjectSummaryComponent
  extends SubscriptionWithDestroyTriggerComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input()
  project: Project;

  firstProduct: Product;
  designAssets: Asset[] = [];
  ipBoardExamples: Asset[] = [];

  @Input()
  placementProductAssets: ProductAsset[] = [];
  placementAssets: Asset[] = [];
  executionAssets: Asset[] = [];

  @Input()
  baseProject: BaseProject;
  @Input()
  submitProjectResult: Subject<any>;
  @Input()
  client: boolean = false;
  @Input()
  fromOldSystem: boolean = false;
  @Output()
  save = new EventEmitter<any>();
  @Output()
  submitProject = new EventEmitter<any>();

  today = new Date();
  projectContacts: ProjectContact[];
  mainContacts: ProjectContact[];
  otherContacts: ProjectContact[];
  extraCompanyContacts: ProjectExtraCompanyContact[];

  showExecutionInfo: boolean = false;
  componentsExecuted: boolean = null;

  ipBoard: IPBoard;

  involvedCompanies: Company[];

  divergentPricesOnly = false;

  constructor(
    private projectDisplayObservableService: ProjectDisplayObservableService
  ) {
    super();
  }

  ngOnInit(): void {
    if (this.placementProductAssets != null) {
      this.placementAssets = this.placementProductAssets.map((a) => a.asset);
    }
    this.calculateComponentsExecuted();
    if (
      this.project != null &&
      this.project.products != null &&
      this.project.products.length > 0 &&
      ProductHelper.isIPBoard(this.project.products[0])
    ) {
      this.ipBoard = this.project.products[0] as IPBoard;
      this.firstProduct = this.formatFirstProduct(this.project.products[0]);
      this.designAssets = this.formatDesignAssets(this.project.products[0]);
      this.ipBoardExamples = this.formatIPBoardExamples(
        <IPBoard>this.project.products[0]
      );
      this.calculateExecutionAssets();
    }

    if (
      this.project.projectContacts != null &&
      this.project.projectContacts.length > 0
    ) {
      this.projectContacts = this.project.projectContacts;
    }
    if (this.project.projectExtraCompanyContacts != null) {
      this.extraCompanyContacts = [...this.project.projectExtraCompanyContacts];
    }

    this.calculateContactsAndInvolvedCompanies();
    this.calculateShowExecutionInfo();

    this.initSubscriptions();
  }

  private calculateComponentsExecuted(): void {
    if (
      this.project == null ||
      !CollectionHelper.isPopulated(this.project.products)
    ) {
      this.componentsExecuted = null;
      return;
    }
    for (const product of this.project.products) {
      if (
        product.productPlacement != null &&
        product.productPlacement.executionComplete != null
      ) {
        this.componentsExecuted = product.productPlacement.executionComplete;
        return;
      }
    }
    this.componentsExecuted = null;
  }

  private calculateExecutionAssets() {
    if (ProjectHelper.NOT_EXECUTED_STATUSES.includes(this.baseProject.status)) {
      return;
    }
    let executionAssets: Asset[] = [];
    this.project.products.forEach((product) => {
      const filteredAssets = product.productAssets.filter(
        (a) => a.formSection === 'execution' && !!a.asset && !a.archived
      );
      executionAssets = executionAssets.concat(
        filteredAssets.map((a) => a.asset)
      );
    });
    this.executionAssets = executionAssets;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['placementProductAssets']) {
      this.placementAssets = [];
      if (changes['placementProductAssets'].currentValue != null) {
        changes['placementProductAssets'].currentValue.forEach((element) => {
          this.placementAssets.push(element.asset);
        });
      }
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  public initSubscriptions() {
    this.addSubscription(
      this.projectDisplayObservableService.getProjectContactsChangedSubscribable(),
      () => {
        this.calculateContactsAndInvolvedCompanies();
      }
    );

    this.addSubscription(
      this.projectDisplayObservableService.getSelectedOtherContactsChangedSubscribable(),
      (contacts: Contact[]) => {
        this.otherContacts = [];
        if (contacts != null) {
          contacts.forEach((c) => {
            const pc = new ProjectContact();
            pc.contact = c;
            pc.company = this.mainContacts[0].company;
            this.otherContacts.push(pc);
          });
        }
        this.projectContacts = [...this.mainContacts].concat(
          this.otherContacts
        );
      }
    );

    this.addSubscription(
      this.projectDisplayObservableService.getNotExecutedHistoryItemAssetsFetchedSubscribable(),
      (event: NotExecutedHistoryItemAssetsFetchedEvent) => {
        if (
          event.subProjectId === this.baseProject.id &&
          event.assets != null
        ) {
          this.executionAssets = event.assets.map((a) => a.asset);
        }
      }
    );

    const formatFirstProductObservable = this.createObservableUntilDestroy(
      this.projectDisplayObservableService.getIPBoardConfigurationChangedSubscribable(),
      this.projectDisplayObservableService.getProductPlacementVariableChangedSubscribable()
    );
    this.addSubscription(formatFirstProductObservable, () => {
      this.ipBoard = this.project.products[0] as IPBoard;
      this.firstProduct = this.formatFirstProduct(this.project.products[0]);
      this.designAssets = this.formatDesignAssets(this.project.products[0]);
      this.ipBoardExamples = this.formatIPBoardExamples(
        <IPBoard>this.project.products[0]
      );
    });

    this.addSubscription(
      this.projectDisplayObservableService.getProductAssetsChangedSubscribable(),
      (event: ProductAssetsChangedEvent) => {
        if (
          event.formSection === 'execution' &&
          ClientBaseProjectSummaryHelper.shouldRecalculateExecutionAssets(
            event,
            this.project.products
          )
        ) {
          this.calculateExecutionAssets();
        }
      }
    );

    const recalculateAssetsObservable = this.createObservableUntilDestroy(
      this.projectDisplayObservableService.getDesignAssetsChangedSubscribable(),
      this.projectDisplayObservableService.getSignSelectedSubscribable()
    );
    this.addSubscription(recalculateAssetsObservable, () => {
      const firstProduct = this.project.products[0];
      this.designAssets = this.formatDesignAssets(firstProduct);
      if (ProductHelper.isIPBoard(firstProduct)) {
        this.ipBoardExamples = this.formatIPBoardExamples(
          <IPBoard>firstProduct
        );
        if (this.firstProduct != null) {
          this.firstProduct.immoscoopLogo =
            this.project.products[0].immoscoopLogo;
          this.firstProduct.spottoLogo = this.project.products[0].spottoLogo;
          this.firstProduct.generalExtraInfo =
            this.project.products[0].generalExtraInfo;
        }
      }
    });

    this.addSubscription(
      this.projectDisplayObservableService.getIPBoardTextFilledSubscribable(),
      () => {
        if (
          this.project != null &&
          this.project.products != null &&
          this.project.products.length > 0 &&
          ProductHelper.isIPBoard(this.project.products[0]) &&
          (this.project.products[0] as IPBoard).boardPart != null
        ) {
          const boardPart = (this.project.products[0] as IPBoard).boardPart;
          const first = this.firstProduct as any;
          if (boardPart.customText != null) {
            first.text = boardPart.customText;
            first.textRight = first.text;
          }
          if (boardPart.otherCustomText != null) {
            first.textRight = boardPart.otherCustomText;
          }
          if (
            first.otherSideBoardPart != null &&
            first.otherSideBoardPart.customText != null
          ) {
            first.textRight = first.otherSideBoardPart.customText;
          }
          this.firstProduct = first;
        }
      }
    );
  }

  private calculateContactsAndInvolvedCompanies() {
    this.mainContacts = this.project.projectContacts;
    this.projectContacts = [...this.mainContacts];
    if (this.otherContacts != null && this.otherContacts.length > 0) {
      this.projectContacts = this.projectContacts.concat(this.otherContacts);
    }
    this.calculateInvolvedCompanies();
  }

  private calculateInvolvedCompanies(): void {
    this.involvedCompanies = ProjectHelper.getInvolvedCompanies(this.project);
  }

  private formatFirstProduct(product: Product) {
    if (ProductHelper.isIPBoard(product) && product.boardPart != null) {
      return formatFirstProduct(product);
    } else {
      return product;
    }
  }

  formatDesignAssets(product: Product) {
    let designAssetsArr: ProductAsset[] = [];
    if (product.productAssets != null && product.productAssets.length > 0) {
      designAssetsArr = product.productAssets.filter(
        (pa) =>
          !pa.archived &&
          pa.asset != null &&
          ProductHelper.DESIGN_INCLUDING_EXTERNAL.includes(pa.formSection)
      );
    }
    if (designAssetsArr.some((pa) => ProductHelper.isDesignAsset(pa))) {
      CollectionHelper.filterInPlace(
        designAssetsArr,
        (pa) => pa.formSection !== 'externalDesign'
      );
    }
    const assetsArr = [];
    designAssetsArr.forEach((a) => {
      assetsArr.push(a.asset);
    });
    return assetsArr;
  }

  formatIPBoardExamples(ipBoard: IPBoard): Asset[] {
    if (ipBoard.boardPart == null || ipBoard.draftDesign) {
      return null;
    }

    const toReturn = [
      IPBoardHelper.getLeftDisplayExample(ipBoard.boardPart),
      IPBoardHelper.getRightDisplayExample(ipBoard),
    ];
    CollectionHelper.filterInPlace(toReturn, (e) => e != null);
    if (toReturn.length === 2 && toReturn[0].uuid === toReturn[1].uuid) {
      toReturn.length = 1;
    }
    return toReturn;
  }

  trackById(index: number, obj: any): number {
    return GeneralHelper.trackById(index, obj);
  }

  calculateShowExecutionInfo() {
    this.showExecutionInfo =
      ProjectHelper.NOT_EXECUTED_STATUSES.includes(this.baseProject.status) ||
      ProjectHelper.getIsExecuted(this.baseProject, this.project);
  }
}
